import { useMemo } from "react"
import { keyBy } from "lodash"
import { IsoDate } from "helpers/types/dates"
import { useModelMetadata } from "resources/models"
import type { FilterOn } from "helpers/filters/filter-ons"
import { useIsOpPro } from "resources/account"
import { SecuritySearchFilter } from "resources/filters"
import { DataStatus, OK, combineTanStackStatuses } from "helpers/data-statuses"
import { useBetaFilters } from "./use-beta-filters"
import { useRiskFilters } from "./use-risk-filters"
import { useClassificationFilters } from "./use-classification-filters"
import { useDescriptorFilters } from "./use-descriptor-filters"
import { useContentSetFilters } from "./use-content-set-filters"
import { useFactorFilters } from "./use-factor-filters"

interface UseFilterOnsParams {
  filter: SecuritySearchFilter | null
  modelId: string | null
  selectedDate: IsoDate
  isHistoricalFactorSensitivityAvailable?: boolean | null
}

export function useFilterOns({ filter, modelId, selectedDate, isHistoricalFactorSensitivityAvailable }: UseFilterOnsParams): [FilterOn[] | null, DataStatus] {
  const [modelMetadata, modelMetadataStatus] = useModelMetadata(modelId)
  const isOpPro = useIsOpPro()

  const allMissingDataFiles = useMemo(() => modelMetadata?.missingDataFiles || [], [modelMetadata])
  const categories = useMemo(() => modelMetadata?.categories || [], [modelMetadata])
  const factors = useMemo(() => {
    const modelFactors = modelMetadata?.factors || []
    return keyBy(modelFactors, "id")
  }, [modelMetadata])

  const [betaFilters, betaStatus] = useBetaFilters({ filter, missingDataFiles: allMissingDataFiles })
  const [riskFilters, riskStatus] = useRiskFilters({ isOpPro: isOpPro ?? false })
  const [classificationFilters, classificationStatus] = useClassificationFilters({ filter, selectedDate })
  const [descriptorFilters, descriptorStatus] = useDescriptorFilters({ modelId })
  const [contentSetFilters, contentSetStatus] = useContentSetFilters()
  const [factorFilters, factorStatus] = useFactorFilters({ categories, factors, filter, isOpPro: isOpPro ?? false, isHistoricalFactorSensitivityAvailable })

  const filterOns = useMemo(() => {
    if (!modelMetadata) {
      return null
    }

    return [
      ...descriptorFilters,
      ...classificationFilters,
      ...betaFilters,
      ...riskFilters,
      ...factorFilters,
      ...contentSetFilters,
    ]
  }, [
    modelMetadata,
    descriptorFilters,
    classificationFilters,
    betaFilters,
    riskFilters,
    factorFilters,
    contentSetFilters,
  ])

  const status: DataStatus = combineTanStackStatuses([
    modelMetadataStatus,
    betaStatus,
    riskStatus,
    classificationStatus,
    descriptorStatus,
    contentSetStatus,
    factorStatus,
  ])

  if (status !== OK) {
    return [null, status]
  }

  return [filterOns, status]
}
